<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import markup from '@/assets/brand-mark.svg?raw'
import { inFlight } from '@/api/activity'

/**
 * The LiverPro MDM logo, drawn from src/assets/brand-mark.svg — the same file every icon in
 * public/ is exported from (tools/make-icons.py), so the sidebar and the browser
 * tab cannot drift apart. Ink, like the rest of Console.
 *
 * It is the one animated element of the panel (docs/UI-GUIDELINES.md §4), a brand
 * gesture in the way the SMG admin's logo is:
 *
 * - when the console wakes up, the L's stem rises, its foot slides in under it and
 *   the P drops into place;
 * - the pointer on it plays that again;
 * - while the console waits on mdm-service the P nods, at most every 1.4 s.
 *
 * None of it runs under `prefers-reduced-motion`; the mark is then simply drawn.
 */
const props = withDefaults(
  defineProps<{
    size?: number
    /** Off where the mark is decoration only, e.g. an error page. */
    animated?: boolean
  }>(),
  { size: 28, animated: true },
)

const svg = computed(() =>
  markup
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace('<svg ', `<svg width="${props.size}" height="${props.size}" aria-hidden="true" `),
)

const root = ref<HTMLElement | null>(null)

// Console motion tokens (src/styles/tokens.css); WAAPI needs them as values.
const EASE_OUT = 'cubic-bezier(0, 0, 0.2, 1)'
const EASE_STANDARD = 'cubic-bezier(0.4, 0, 0.2, 1)'
const PULSE_EVERY_MS = 1400

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

let running: Animation[] = []
let lastPulse = 0
let busyTimer: number | undefined

function parts() {
  const el = root.value
  return {
    stem: el?.querySelector<SVGElement>('.bm-stem') ?? null,
    bowl: el?.querySelector<SVGElement>('.bm-bowl') ?? null,
    foot: el?.querySelector<SVGElement>('.bm-foot') ?? null,
  }
}

function canMove(): boolean {
  return props.animated && !reducedMotion.matches && running.every((a) => a.playState === 'finished')
}

/** Every part moves from its own box: the stem grows from its foot, the P from its middle. */
function prepare(el: SVGElement, origin: string) {
  el.style.transformBox = 'fill-box'
  el.style.transformOrigin = origin
}

/** The full gesture: stem, then foot, then the P. About 0.7 s. */
function play() {
  if (!canMove()) {
    return
  }
  const { stem, bowl, foot } = parts()
  const next: Animation[] = []
  if (stem) {
    prepare(stem, '50% 100%')
    next.push(stem.animate(
      { transform: ['scaleY(0)', 'scaleY(1)'] },
      { duration: 360, easing: EASE_OUT, fill: 'backwards' },
    ))
  }
  if (foot) {
    prepare(foot, '0% 50%')
    next.push(foot.animate(
      { opacity: [0, 1], transform: ['scaleX(0.3)', 'none'] },
      { duration: 300, delay: 160, easing: EASE_OUT, fill: 'backwards' },
    ))
  }
  if (bowl) {
    prepare(bowl, '50% 50%')
    next.push(bowl.animate(
      { opacity: [0, 1], transform: ['translateY(-30%) scale(0.8)', 'none'] },
      { duration: 320, delay: 360, easing: EASE_OUT, fill: 'backwards' },
    ))
  }
  running = next
}

/** Working: the P nods down and back. */
function pulse() {
  if (!canMove() || Date.now() - lastPulse < PULSE_EVERY_MS) {
    return
  }
  lastPulse = Date.now()
  const { bowl } = parts()
  if (!bowl) {
    return
  }
  prepare(bowl, '50% 50%')
  running = [bowl.animate(
    { transform: ['none', 'translateY(8%)', 'none'] },
    { duration: 420, easing: EASE_STANDARD },
  )]
}

watch(
  () => inFlight.value > 0,
  (busy) => {
    window.clearInterval(busyTimer)
    if (busy) {
      pulse()
      busyTimer = window.setInterval(pulse, PULSE_EVERY_MS)
    }
  },
)

onMounted(play)

onUnmounted(() => {
  window.clearInterval(busyTimer)
  running.forEach((animation) => animation.cancel())
})
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -- a bundled, trusted asset -->
  <span ref="root" class="brand-mark" @mouseenter="play" v-html="svg" />
</template>

<style scoped>
.brand-mark { flex: 0 0 auto; display: inline-flex; color: var(--ink-1000); }
</style>
